import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { useAdminGetDietApplicationsQuery, useAdminGetDietQuery } from "../../api/examsApi";

export default function AdminDietAttendancePage() {
  const { id: examId = "", dietId = "" } = useParams();

  const { data: diet } = useAdminGetDietQuery(dietId, { skip: !dietId });
  const { data: raw = [], isLoading } = useAdminGetDietApplicationsQuery(
    { dietId },
    { skip: !dietId }
  );

  // Only include applications that have an exam number
  const applications: any[] = (raw as any[]).filter((a) => a.exam_no);

  // Sort by exam_no, then name
  const sorted = [...applications].sort((a, b) => {
    const na = a.exam_no ?? "";
    const nb = b.exam_no ?? "";
    if (na < nb) return -1;
    if (na > nb) return 1;
    return (a.user?.name ?? "").localeCompare(b.user?.name ?? "");
  });

  // Group by center name for sectioned output
  const grouped: Record<string, any[]> = {};
  for (const app of sorted) {
    const key = app.center?.name ?? "No Center Assigned";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(app);
  }

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { font-size: 11px; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; }
          .center-section { page-break-inside: avoid; margin-bottom: 2rem; }
        }
      `}</style>

      <div className="p-6 bg-white min-h-screen">
        {/* Toolbar */}
        <div className="no-print flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to={`/exams/admin/exams/${examId}/diets/${dietId}`}
              className="p-2 rounded-xl hover:bg-slate-100 transition-all"
            >
              <ArrowLeft className="w-4 h-4 text-slate-600" />
            </Link>
            <div>
              <h1 className="text-lg font-black text-slate-900 uppercase tracking-widest">
                Attendance Sheet
              </h1>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">
                {diet?.title ?? "Loading…"} &nbsp;·&nbsp; {sorted.length} candidate{sorted.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <button
            onClick={() => window.print()}
            disabled={isLoading || sorted.length === 0}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 disabled:opacity-50 transition-all"
          >
            <Printer className="w-4 h-4" />
            Print Sheet
          </button>
        </div>

        {/* Print header */}
        <div className="mb-6 border-b-2 border-slate-900 pb-3">
          <h2 className="text-xl font-black uppercase tracking-widest text-slate-900">
            Examination Attendance Sheet
          </h2>
          <p className="text-sm font-bold text-slate-600 uppercase tracking-widest mt-1">
            {diet?.title ?? ""}
          </p>
        </div>

        {isLoading && (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
          </div>
        )}

        {!isLoading && sorted.length === 0 && (
          <div className="text-center py-20">
            <p className="text-slate-400 font-black uppercase tracking-widest text-sm">
              No candidates with exam numbers found.
            </p>
          </div>
        )}

        {/* Sections per center */}
        {Object.entries(grouped).map(([centerName, apps]) => (
          <div key={centerName} className="center-section mb-10">
            <div className="bg-slate-900 text-white px-4 py-2 mb-3 flex items-center justify-between">
              <span className="font-black text-xs uppercase tracking-widest">{centerName}</span>
              <span className="font-bold text-xs">{apps.length} candidate{apps.length !== 1 ? "s" : ""}</span>
            </div>

            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-slate-900">
                  <th className="text-left py-2 px-3 font-black text-[10px] uppercase tracking-wider text-slate-600 w-8">#</th>
                  <th className="text-left py-2 px-3 font-black text-[10px] uppercase tracking-wider text-slate-600">Exam No</th>
                  <th className="text-left py-2 px-3 font-black text-[10px] uppercase tracking-wider text-slate-600">Candidate Name</th>
                  <th className="text-left py-2 px-3 font-black text-[10px] uppercase tracking-wider text-slate-600">Membership No</th>
                  <th className="text-left py-2 px-3 font-black text-[10px] uppercase tracking-wider text-slate-600">Exam</th>
                  <th className="text-left py-2 px-3 font-black text-[10px] uppercase tracking-wider text-slate-600 no-print">Status</th>
                  <th className="py-2 px-3 font-black text-[10px] uppercase tracking-wider text-slate-600 text-center w-24">Signature</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app, idx) => (
                  <tr key={app.id} className={idx % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                    <td className="py-2 px-3 text-slate-400 font-bold text-xs">{idx + 1}</td>
                    <td className="py-2 px-3 font-black text-xs text-slate-900 uppercase">{app.exam_no}</td>
                    <td className="py-2 px-3 font-bold text-xs text-slate-800 uppercase">{app.user?.name ?? "—"}</td>
                    <td className="py-2 px-3 font-bold text-xs text-slate-600">{app.user?.member?.membership_no ?? "—"}</td>
                    <td className="py-2 px-3">
                      <span className="font-black text-[10px] text-slate-700 uppercase tracking-wider">{app.exam?.code}</span>
                    </td>
                    <td className="py-2 px-3 no-print">
                      <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-slate-200 text-slate-500">
                        {app.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 border-b border-slate-300 w-24" />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {/* Footer */}
        {sorted.length > 0 && (
          <div className="mt-10 pt-4 border-t border-slate-200 text-[10px] text-slate-400 font-bold uppercase tracking-widest flex justify-between">
            <span>Total Candidates: {sorted.length}</span>
            <span>Generated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</span>
          </div>
        )}
      </div>
    </>
  );
}
